"use client";

import { c as _c } from "react/compiler-runtime";
import type { FC, PointerEventHandler, ReactNode } from "react";
import { Fragment, useEffect, useLayoutEffect, useRef, useState } from "react";
import { FaStar } from "react-icons/fa";
import type { ComponentPropsWithoutChildren } from "@/lib/component-props";
import { cn } from "@/lib/utils";
import { TICKER_DRAG_THRESHOLD_PX, TICKER_LOOP_DURATION_MS, TICKER_RESUME_DELAY_MS, tickerCopyCount, wrapOffset } from "./ticker-motion";

/** Separator accent colours (Pencil Marquee Banner cycle). */
const STAR_COLORS = ["#FFE156", "#FF6B6B", "#7ED957", "#A8D8EA"] as const;
export type TickerItemSlot = {
  id: string;
  node: ReactNode;
};
export interface TickerProps extends ComponentPropsWithoutChildren<"div"> {
  items: TickerItemSlot[];
}
type DragSession = {
  pointerId: number | null;
  startX: number;
  startY: number;
  lastX: number;
  active: boolean;
  didDrag: boolean;
};
const createDragSession = (): DragSession => ({
  pointerId: null,
  startX: 0,
  startY: 0,
  lastX: 0,
  active: false,
  didDrag: false
});
const writeTrackOffset = (track: HTMLDivElement | null, offsetRef: {
  current: number;
}, next: number) => {
  offsetRef.current = next;
  if (track) {
    track.style.transform = `translate3d(${next}px, 0, 0)`;
  }
};
const clearTrackOffset = (track: HTMLDivElement | null, offsetRef: {
  current: number;
}) => {
  offsetRef.current = 0;
  track?.style.removeProperty("transform");
};
const renderSequence = (items: TickerItemSlot[], keyPrefix: string) => items.map(({
  id,
  node
}, index) => <Fragment key={`${keyPrefix}-${id}`}>
      {node}
      <FaStar aria-hidden className="h-2.5 w-2.5 shrink-0 lg:h-5 lg:w-5" style={{
    color: STAR_COLORS[index % STAR_COLORS.length]
  }} />
    </Fragment>);
const prefersReducedMotion = (): boolean => typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
const tickerCopyKey = (copyIndex: number): string => `ticker-copy-${String(copyIndex)}`;

/**
 * Horizontal ticker. Animates an infinite marquee. At the desktop breakpoint
 * an edge fade keeps words from clipping mid-glyph. Drag left or right to
 * scrub; auto-scroll resumes after a short pause. Under
 * `prefers-reduced-motion`, shows a wrapping static row with no animation
 * or drag.
 */
export const Ticker: FC<TickerProps> = t0 => {
  const $ = _c(59);
  let className;
  let items;
  let props;
  if ($[0] !== t0) {
    ({
      items,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = items;
    $[3] = props;
  } else {
    className = $[1];
    items = $[2];
    props = $[3];
  }
  const viewportRef = useRef(null);
  const trackRef = useRef(null);
  const stripRef = useRef(null);
  const offsetRef = useRef(0);
  const stripWidthRef = useRef(0);
  const resumeTimeoutRef = useRef(null);
  let t1;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = createDragSession();
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const dragRef = useRef(t1);
  const [copyCount, setCopyCount] = useState(2);
  const [stripWidth, setStripWidth] = useState(0);
  const [paused, setPaused] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [reduceMotion, setReduceMotion] = useState(false);
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      if (resumeTimeoutRef.current !== null) {
        window.clearTimeout(resumeTimeoutRef.current);
      }
      resumeTimeoutRef.current = window.setTimeout(() => {
        resumeTimeoutRef.current = null;
        setPaused(false);
      }, TICKER_RESUME_DELAY_MS);
    };
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const scheduleResume = t2;
  let t3;
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      const media = window.matchMedia("(prefers-reduced-motion: reduce)");
      const sync = () => {
        setReduceMotion(media.matches);
      };
      sync();
      media.addEventListener("change", sync);
      return () => {
        media.removeEventListener("change", sync);
      };
    };
    t4 = [];
    $[6] = t3;
    $[7] = t4;
  } else {
    t3 = $[6];
    t4 = $[7];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[8] !== items.length) {
    t5 = () => {
      if (items.length === 0) {
        return;
      }
      const viewport = viewportRef.current;
      const strip = stripRef.current;
      if (!viewport || !strip) {
        return;
      }
      const measure = () => {
        const nextStripWidth = strip.getBoundingClientRect().width;
        const nextViewportWidth = viewport.getBoundingClientRect().width;
        stripWidthRef.current = nextStripWidth;
        setStripWidth(nextStripWidth);
        setCopyCount(tickerCopyCount(nextViewportWidth, nextStripWidth));
        writeTrackOffset(trackRef.current, offsetRef, wrapOffset(offsetRef.current, nextStripWidth));
      };
      measure();
      if (typeof ResizeObserver === "undefined") {
        return;
      }
      const observer = new ResizeObserver(measure);
      observer.observe(viewport);
      observer.observe(strip);
      return () => observer.disconnect();
    };
    t6 = [items.length];
    $[8] = items.length;
    $[9] = t5;
    $[10] = t6;
  } else {
    t5 = $[9];
    t6 = $[10];
  }
  useLayoutEffect(t5, t6);
  let t7;
  let t8;
  if ($[11] !== reduceMotion) {
    t7 = () => {
      if (!reduceMotion) {
        return;
      }
      clearTrackOffset(trackRef.current, offsetRef);
      setPaused(false);
      setIsDragging(false);
      dragRef.current = createDragSession();
      if (resumeTimeoutRef.current !== null) {
        window.clearTimeout(resumeTimeoutRef.current);
        resumeTimeoutRef.current = null;
      }
    };
    t8 = [reduceMotion];
    $[11] = reduceMotion;
    $[12] = t7;
    $[13] = t8;
  } else {
    t7 = $[12];
    t8 = $[13];
  }
  useEffect(t7, t8);
  let t10;
  let t9;
  if ($[14] !== paused || $[15] !== reduceMotion || $[16] !== stripWidth) {
    t9 = () => {
      if (reduceMotion || paused || stripWidth <= 0) {
        return;
      }
      const track = trackRef.current;
      if (!track) {
        return;
      }
      let frame = 0;
      let lastTime = performance.now();
      const pixelsPerMs = stripWidth / TICKER_LOOP_DURATION_MS;
      const tick = now => {
        const delta = Math.min(now - lastTime, 64);
        lastTime = now;
        writeTrackOffset(track, offsetRef, wrapOffset(offsetRef.current - pixelsPerMs * delta, stripWidth));
        frame = window.requestAnimationFrame(tick);
      };
      frame = window.requestAnimationFrame(tick);
      return () => {
        window.cancelAnimationFrame(frame);
      };
    };
    t10 = [paused, reduceMotion, stripWidth];
    $[14] = paused;
    $[15] = reduceMotion;
    $[16] = stripWidth;
    $[17] = t10;
    $[18] = t9;
  } else {
    t10 = $[17];
    t9 = $[18];
  }
  useEffect(t9, t10);
  let t11;
  let t12;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => () => {
      if (resumeTimeoutRef.current !== null) {
        window.clearTimeout(resumeTimeoutRef.current);
      }
    };
    t12 = [];
    $[19] = t11;
    $[20] = t12;
  } else {
    t11 = $[19];
    t12 = $[20];
  }
  useEffect(t11, t12);
  if (items.length === 0) {
    return null;
  }
  let t13;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = event => {
      const drag = dragRef.current;
      if (!drag.active || drag.pointerId !== event.pointerId) {
        return;
      }
      if (drag.didDrag) {
        if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
          event.currentTarget.releasePointerCapture(event.pointerId);
        }
        setIsDragging(false);
        scheduleResume();
      }
      dragRef.current = createDragSession();
    };
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  const endDrag = t13;
  let t14;
  if ($[22] !== reduceMotion) {
    t14 = event_0 => {
      if (reduceMotion || prefersReducedMotion() || event_0.button !== 0) {
        return;
      }
      if (resumeTimeoutRef.current !== null) {
        window.clearTimeout(resumeTimeoutRef.current);
        resumeTimeoutRef.current = null;
      }
      dragRef.current = {
        pointerId: event_0.pointerId,
        startX: event_0.clientX,
        startY: event_0.clientY,
        lastX: event_0.clientX,
        active: true,
        didDrag: false
      };
    };
    $[22] = reduceMotion;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  const handlePointerDown = t14;
  let t15;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = event_1 => {
      const drag_0 = dragRef.current;
      if (!drag_0.active || drag_0.pointerId !== event_1.pointerId) {
        return;
      }
      const totalX = event_1.clientX - drag_0.startX;
      const totalY = event_1.clientY - drag_0.startY;
      if (!drag_0.didDrag) {
        if (Math.abs(totalX) < TICKER_DRAG_THRESHOLD_PX && Math.abs(totalY) < TICKER_DRAG_THRESHOLD_PX) {
          return;
        }
        if (Math.abs(totalY) >= Math.abs(totalX)) {
          dragRef.current = createDragSession();
          return;
        }
        drag_0.didDrag = true;
        setPaused(true);
        setIsDragging(true);
        event_1.currentTarget.setPointerCapture?.(event_1.pointerId);
        writeTrackOffset(trackRef.current, offsetRef, wrapOffset(offsetRef.current + totalX, stripWidthRef.current));
        drag_0.lastX = event_1.clientX;
        return;
      }
      const deltaX = event_1.clientX - drag_0.lastX;
      drag_0.lastX = event_1.clientX;
      writeTrackOffset(trackRef.current, offsetRef, wrapOffset(offsetRef.current + deltaX, stripWidthRef.current));
    };
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  const handlePointerMove = t15;
  let t16;
  let t17;
  let t18;
  let t19;
  let t20;
  let t21;
  let t22;
  let t23;
  let t24;
  let t25;
  if ($[25] !== className || $[26] !== copyCount || $[27] !== handlePointerDown || $[28] !== isDragging || $[29] !== items || $[30] !== props || $[31] !== reduceMotion) {
    const copies = Array.from({
      length: copyCount
    }, _temp);
    t23 = viewportRef;
    if ($[42] !== className) {
      t24 = cn("ticker-fade overflow-hidden bg-[var(--fg-primary)] text-[var(--fg-inverse)] motion-reduce:overflow-visible", className);
      $[42] = className;
      $[43] = t24;
    } else {
      t24 = $[43];
    }
    t25 = props;
    t16 = trackRef;
    const t26 = reduceMotion ? "cursor-default" : isDragging ? "cursor-grabbing select-none" : "cursor-grab select-none touch-pan-y";
    if ($[44] !== t26) {
      t17 = cn("ticker-track flex w-max items-center py-3 lg:py-4 motion-reduce:w-full motion-reduce:flex-wrap motion-reduce:justify-center motion-reduce:gap-x-4 motion-reduce:gap-y-2 motion-reduce:px-4 motion-reduce:py-3", t26);
      $[44] = t26;
      $[45] = t17;
    } else {
      t17 = $[45];
    }
    t18 = reduceMotion ? undefined : endDrag;
    t19 = reduceMotion ? undefined : handlePointerDown;
    t20 = reduceMotion ? undefined : handlePointerMove;
    t21 = reduceMotion ? undefined : endDrag;
    t22 = copies.map(t27 => {
      const {
        key,
        copyIndex: copyIndex_0
      } = t27;
      return <div key={key} ref={copyIndex_0 === 0 ? stripRef : undefined} aria-hidden={copyIndex_0 > 0} data-ticker-strip={copyIndex_0} className={cn("flex shrink-0 flex-nowrap items-center gap-4 pr-4 lg:gap-12 lg:pr-12", copyIndex_0 > 0 && "motion-reduce:hidden")}>{renderSequence(items, key)}</div>;
    });
    $[25] = className;
    $[26] = copyCount;
    $[27] = handlePointerDown;
    $[28] = isDragging;
    $[29] = items;
    $[30] = props;
    $[31] = reduceMotion;
    $[32] = t16;
    $[33] = t17;
    $[34] = t18;
    $[35] = t19;
    $[36] = t20;
    $[37] = t21;
    $[38] = t22;
    $[39] = t23;
    $[40] = t24;
    $[41] = t25;
  } else {
    t16 = $[32];
    t17 = $[33];
    t18 = $[34];
    t19 = $[35];
    t20 = $[36];
    t21 = $[37];
    t22 = $[38];
    t23 = $[39];
    t24 = $[40];
    t25 = $[41];
  }
  let t26;
  if ($[46] !== t16 || $[47] !== t17 || $[48] !== t18 || $[49] !== t19 || $[50] !== t20 || $[51] !== t21 || $[52] !== t22) {
    t26 = <div ref={t16} className={t17} onPointerCancel={t18} onPointerDown={t19} onPointerMove={t20} onPointerUp={t21}>{t22}</div>;
    $[46] = t16;
    $[47] = t17;
    $[48] = t18;
    $[49] = t19;
    $[50] = t20;
    $[51] = t21;
    $[52] = t22;
    $[53] = t26;
  } else {
    t26 = $[53];
  }
  let t27;
  if ($[54] !== t23 || $[55] !== t24 || $[56] !== t25 || $[57] !== t26) {
    t27 = <div ref={t23} className={t24} {...t25}>{t26}</div>;
    $[54] = t23;
    $[55] = t24;
    $[56] = t25;
    $[57] = t26;
    $[58] = t27;
  } else {
    t27 = $[58];
  }
  return t27;
};
function _temp(_, copyIndex) {
  return {
    key: tickerCopyKey(copyIndex),
    copyIndex
  };
}